import React from 'react';
import PhotoCaptureModalEnhanced from './PhotoCaptureModalEnhanced';
import { enhancedPhotoProps } from '../utils/photoModalProps';
import type { PhotoCaptureResult } from '../types/photo';

/**
 * "Captura via Webcam" — the shared photo modal, opened on its webcam tab.
 *
 * This used to be a modal of its own: a capture screen, and then a SECOND cropper with its
 * own zoom and rotation. So which editor an operator got depended on how the photo was taken
 * — a visitor photo was edited somewhere other than a morador photo, and only one of the two
 * had the mask, "Selecionar Área", the rotate/flip buttons and the fill-on-open rule. Every
 * improvement had to be made twice or it silently applied to half the product.
 *
 * PhotoUploadModal had already been reduced to this shape; this is the same move for the
 * webcam.
 *
 * The IP tab stays hidden (enhancedPhotoProps hides it when there is no customerId) and that
 * is deliberate, not an oversight: PhotoIPCameraModal owns a live RTSP/MJPEG preview that the
 * enhanced modal's IP tab does not have — it takes a blind snapshot instead. Cameras keep
 * their dedicated way in.
 */

export type PhotoResult = PhotoCaptureResult;

interface PhotoWebcamModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: PhotoResult) => void;
  title?: string;
  cropShape?: 'round' | 'rect';
  aspectRatio?: number;
  darkMode?: boolean;
  /** Skip the face-detection affordances — a vehicle or a document has no face. */
  disableFaceDetection?: boolean;
}

const PhotoWebcamModal: React.FC<PhotoWebcamModalProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Captura via Webcam',
  cropShape = 'rect',
  aspectRatio = 4 / 5,
  darkMode = false,
  disableFaceDetection = false,
}) => (
  <PhotoCaptureModalEnhanced
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    darkMode={darkMode}
    {...enhancedPhotoProps('webcam', {
      title,
      cropShape,
      aspectRatio,
      disableFaceDetection,
    })}
  />
);

export default PhotoWebcamModal;
